// frontend/src/hooks/useProductionSequence.ts
import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import type {
  ProductionOrder, LineId, LineConfig, ColumnFilter, ColumnFilterKind, SortField, SortDirection,
  PriorityConfig, RecalcResult, CalendarDay,
} from '@/types/productionSequence';
import { toggleDayOverride } from '@/utils/calendarUtils';
import type { OverlayBody } from '@/types';
import { REORDERABLE_STATUSES, FINISHED_STATUSES } from '@/types/productionSequence';
import { secuenciaApi, inventarioApi, auditApi } from '@/services/api';
import { mapRowToOrder, attachComponentes } from '@/utils/secuenciaAdapter';
import {
  sortOrders, sortByCriterios, recalcComponentStatus, computeKpis, buildPools,
  calcularCapacidadDiaria, recalcularSecuencia, buildCalendarMap,
  type ComponentPools,
} from '@/utils/sequenceCalculations';
import { seedCalendar2026, defaultLineConfig } from '@/mocks/sequenceSeed';

/** Línea sintética que representa "todas las líneas juntas". */
export const ALL_LINES = '__ALL__';
/** Línea (rutaPrl) que se aísla en la pestaña "Fabricación". */
export const FABRICACION_LINE = 'FABRICACION';
export type SequenceMode = 'produccion' | 'fabricacion';

export const defaultPriorityConfig: PriorityConfig = {
  criterios: [
    { field: 'fechaPlanificadaFin', label: 'sequence.col.fPlanFin', direction: 'asc',  active: true,  order: 0 },
    { field: 'subfamilia',          label: 'sequence.col.subfamilia', direction: 'asc', active: true,  order: 1 },
    { field: 'acabado',             label: 'sequence.col.acabado',  direction: 'asc',  active: true,  order: 2 },
    { field: 'estado',              label: 'sequence.col.estado',   direction: 'asc',  active: false, order: 3 },
    { field: 'udOT',                label: 'sequence.col.udOT',     direction: 'desc', active: false, order: 4 },
    { field: 'referencia',          label: 'sequence.col.referencia', direction: 'asc', active: false, order: 5 },
    { field: 'familia',             label: 'sequence.col.familia',  direction: 'asc',  active: false, order: 6 },
    { field: 'fechaPedido',         label: 'sequence.col.fPedido',  direction: 'asc',  active: false, order: 7 },
  ],
};

const FALLBACK_CONFIG: LineConfig = { lineId: ALL_LINES, turnos: 1, udTurnoPromedio: 2000, upm: 150 };

function matchesFilter(o: ProductionOrder, f: ColumnFilter): boolean {
  if (!f.enabled || !f.value) return true;
  const raw = o[f.field] as unknown;
  // La rama se decide por el KIND de la columna, no por la puntuación del valor:
  // así un texto libre con "," o "|" no se interpreta como set/rango.
  switch (f.kind) {
    case 'number': {
      // Rango "min|max". Campo no numérico/null → no pasa el filtro.
      if (typeof raw !== 'number') return false;
      const [min, max] = f.value.split('|');
      if (min !== '' && raw < Number(min)) return false;
      if (max !== '' && raw > Number(max)) return false;
      return true;
    }
    case 'set': {
      // Membresía exacta sobre el conjunto seleccionado "a,b,c".
      const set = f.value.split(',').filter(Boolean);
      return set.includes(String(raw ?? ''));
    }
    case 'text':
    default:
      return String(raw ?? '').toLowerCase().includes(f.value.toLowerCase());
  }
}

export interface UseProductionSequenceReturn {
  orders: ProductionOrder[];
  visibleOrders: ProductionOrder[];
  selectedLine: LineId;
  lineConfig: LineConfig;
  kpis: ReturnType<typeof computeKpis>;
  isCustomOrder: boolean;
  isReorderMode: boolean;
  isLoading: boolean;
  isError: boolean;
  columnFilters: ColumnFilter[];
  activeSort: { field: SortField; direction: SortDirection } | null;
  priorityConfig: PriorityConfig;
  setSelectedLine: (line: LineId) => void;
  setReorderMode: (on: boolean) => void;
  setColumnFilter: (field: SortField, kind: ColumnFilterKind, value: string) => void;
  clearColumnFilter: (field: SortField) => void;
  clearAllFilters: () => void;
  setColumnSort: (field: SortField, direction: SortDirection) => void;
  applyPriorityConfig: (config: PriorityConfig) => void;
  resetToAutoOrder: () => void;
  reorderByDragDrop: (fromId: string, toId: string) => void;
  updateEditableField: (orderId: string, field: 'comentarios' | 'campoCalidad', value: string | boolean | null) => void;
  pendingSync: Set<string>;
  updateLineConfig: (partial: Partial<LineConfig>) => void;
  lineConfigs: Record<LineId, LineConfig>;
  updateLineConfigFor: (lineId: LineId, partial: Partial<LineConfig>) => void;
  selectedOrderId: string | null;
  selectedOrder: ProductionOrder | null;
  selectOrder: (id: string | null) => void;
  triggerRecalc: (orderId: string) => void;
  resetRecalc: () => void;
  isRecalcPending: boolean;
  lastRecalcResults: RecalcResult[];
  ordersAtRisk: Set<string>;
  canRecalc: boolean;
  calendarDays: CalendarDay[];
  toggleCalendarDay: (date: string) => void;
  resetCalendar: () => void;
  lines: LineId[];
  sequenceMode: SequenceMode;
  setSequenceMode: (mode: SequenceMode) => void;
}

export function useProductionSequence(): UseProductionSequenceReturn {
  // ── Datos remotos (React Query) ───────────────────────────────────────────
  const secQ = useQuery({ queryKey: ['secuencia'], queryFn: () => secuenciaApi.getSecuencia(), staleTime: 5 * 60 * 1000 });
  const compQ = useQuery({ queryKey: ['secuencia-componentes'], queryFn: () => secuenciaApi.getComponentes(), staleTime: 5 * 60 * 1000 });
  const invQ = useQuery({ queryKey: ['inventario-secuencia'], queryFn: () => inventarioApi.getInventario(), staleTime: 5 * 60 * 1000 });
  // El calendario de llegadas: lo que permite preguntar «¿llega a tiempo?» y no
  // sólo «¿hay bastante?». Son ~1.600 filas y viaja entero, porque el corte
  // depende de la secuencia y esa la cambia el usuario arrastrando.
  const entradasQ = useQuery({ queryKey: ['entradas-previstas'], queryFn: () => inventarioApi.getEntradasPrevistas(), staleTime: 5 * 60 * 1000 });
  // Overlay (Dataverse): sondeo ligero cada 15 s para ver ediciones de otros casi
  // en tiempo real, sin reconstruir el join Fabric. Solo cuando la pestaña está visible.
  const overlayQ = useQuery({
    queryKey: ['secuencia-overlay'],
    queryFn: () => secuenciaApi.getOverlay(),
    refetchInterval: 15_000,
    refetchIntervalInBackground: false,
    staleTime: 10_000,
  });

  const pools: ComponentPools = useMemo(() => {
    // Si el calendario no llegó, `undefined` y NO lista vacía: una lista vacía
    // diría «no hay ninguna compra» y pintaría de rojo media pantalla. El
    // cálculo lo distingue y cae al camino de sólo-cantidad, que es como
    // decidía antes de tener fechas.
    const entradas = entradasQ.data?.data?.map(e => ({
      empresa: e.dataareaid,
      itemid: e.itemid,
      color: e.color,
      estilo: e.estilo,
      almacen: e.almacen,
      fecha: e.fechaEntrega,
      qty: e.qtyPendiente,
    }));
    return buildPools(invQ.data?.data ?? [], entradas);
  }, [invQ.data, entradasQ.data]);
  // Ref para que los callbacks vean siempre los pools actuales sin recrearse.
  const poolsRef = useRef(pools);
  poolsRef.current = pools;

  // Secuencia base: adapter + componentes + orden automático + recálculo real.
  const baseOrders = useMemo(() => {
    const rows = secQ.data?.data ?? [];
    const comps = compQ.data?.data ?? [];
    const adapted = attachComponentes(rows.map(mapRowToOrder), comps);
    return recalcComponentStatus(sortOrders(adapted), pools);
  }, [secQ.data, compQ.data, pools]);

  // ── Estado en memoria ─────────────────────────────────────────────────────
  const [orders, setOrders] = useState<ProductionOrder[]>([]);
  // Selección de UNA única línea (no se permite ver todas ni varias a la vez).
  const [selectedLine, setSelectedLineState] = useState<LineId>(ALL_LINES);
  const [sequenceMode, setSequenceModeState] = useState<SequenceMode>('produccion');
  const [lineConfigs, setLineConfigs] = useState<Record<LineId, LineConfig>>({});
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const [isRecalcPending, setIsRecalcPending] = useState(false);
  const [lastRecalcResults, setLastRecalcResults] = useState<RecalcResult[]>([]);
  // Calendario laboral editable (en memoria, mock). El array son solo excepciones;
  // el Map derivado es lo que consume el recálculo. Futuro: sembrar desde Dataverse.
  const [calendarDays, setCalendarDays] = useState<CalendarDay[]>(seedCalendar2026);
  const calendar = useMemo(() => buildCalendarMap(calendarDays), [calendarDays]);
  const originalDatesRef = useRef<Map<string, string | null>>(new Map());
  const [isCustomOrder, setIsCustomOrder] = useState(false);
  const [isReorderMode, setIsReorderMode] = useState(false);
  const [columnFilters, setColumnFilters] = useState<ColumnFilter[]>([]);
  const [activeSort, setActiveSort] = useState<{ field: SortField; direction: SortDirection } | null>(null);
  const [pendingSync, setPendingSync] = useState<Set<string>>(new Set());
  const [priorityConfig, setPriorityConfig] = useState<PriorityConfig>(defaultPriorityConfig);

  // Sincroniza el estado local con los datos remotos (NO es un cálculo derivado:
  // siembra la secuencia editable desde el fetch). No pisa un orden personalizado.
  useEffect(() => {
    if (!isCustomOrder) setOrders(baseOrders);
  }, [baseOrders, isCustomOrder]);

  // Siembra config por línea + snapshot de fechas originales, una sola vez al
  // llegar los datos. Las fechas originales sirven para reset y delta.
  const didSeedConfigs = useRef(false);
  useEffect(() => {
    if (didSeedConfigs.current || baseOrders.length === 0) return;
    const byLine: Record<string, ProductionOrder[]> = {};
    for (const o of baseOrders) (byLine[o.lineId] ??= []).push(o);
    const seeded: Record<LineId, LineConfig> = {};
    for (const [lineId, ords] of Object.entries(byLine)) seeded[lineId] = defaultLineConfig(lineId, ords);
    setLineConfigs(seeded);
    const dates = new Map<string, string | null>();
    for (const o of baseOrders) dates.set(o.id, o.fechaPlanificadaFin);
    originalDatesRef.current = dates;
    didSeedConfigs.current = true;
  }, [baseOrders]);

  // Ref a pendingSync para que el merge del overlay lea el estado actual sin
  // re-ejecutarse al limpiarse un indicador (evita revertir ediciones recientes).
  const pendingSyncRef = useRef(pendingSync);
  pendingSyncRef.current = pendingSync;

  // Fusiona el overlay sondeado (Dataverse) en los 2 campos editables, SIN
  // reordenar ni pisar lo que el usuario está editando (pending-sync). Parchea
  // solo cuando el valor difiere → sin churn de render.
  // `motivoDesviacion` NO entra aquí: lo sirve la vista Fabric (consulta
  // ['secuencia']) y es de solo lectura.
  useEffect(() => {
    const ov = overlayQ.data?.data;
    if (!ov) return;
    const byId = new Map(ov.map(r => [r.produccion, r]));
    const pend = pendingSyncRef.current;
    setOrders(prev => prev.map(o => {
      const r = byId.get(o.id);
      if (!r) return o;
      const patch: Partial<ProductionOrder> = {};
      const nextCom = r.comentarios ?? '';
      const nextCal = r.campoCalidad ?? null;
      if (!pend.has(`${o.id}:comentarios`) && nextCom !== o.comentarios) patch.comentarios = nextCom;
      if (!pend.has(`${o.id}:campoCalidad`) && nextCal !== o.campoCalidad) patch.campoCalidad = nextCal;
      return Object.keys(patch).length ? { ...o, ...patch } : o;
    }));
  }, [overlayQ.data]);

  // Líneas dinámicas desde rutaPrl (+ "Todas").
  const lines = useMemo(() => {
    const set = new Set<string>();
    for (const o of baseOrders) set.add(o.lineId);
    const real = [...set].sort((a, b) => a.localeCompare(b, 'es-ES'));
    // Fabricación: solo la línea FABRICACION (sin "Todas"). Producción: el resto + "Todas".
    if (sequenceMode === 'fabricacion') return real.filter(l => l === FABRICACION_LINE);
    return [ALL_LINES, ...real.filter(l => l !== FABRICACION_LINE)];
  }, [baseOrders, sequenceMode]);

  // Default de línea: fija la primera línea real UNA sola vez al llegar datos.
  // No vuelve a tocar selectedLine en refetches (no pisa la elección del usuario).
  const didInitLine = useRef(false);
  useEffect(() => {
    if (didInitLine.current) return;
    const real = lines.filter(l => l !== ALL_LINES);
    if (real.length) { setSelectedLineState(real[0]); didInitLine.current = true; }
  }, [lines]);

  // ── Acciones ──────────────────────────────────────────────────────────────
  const setSelectedLine = useCallback((line: LineId) => setSelectedLineState(line), []);

  // Cambio de pestaña Producción/Fabricación: ajusta la línea seleccionada al nuevo
  // ámbito (Fabricación → FABRICACION; Producción → primera línea no-FAB, o "Todas").
  const setSequenceMode = useCallback((mode: SequenceMode) => {
    setSequenceModeState(mode);
    if (mode === 'fabricacion') { setSelectedLineState(FABRICACION_LINE); return; }
    const real = [...new Set(baseOrders.map(o => o.lineId))]
      .filter(l => l !== FABRICACION_LINE)
      .sort((a, b) => a.localeCompare(b, 'es-ES'));
    setSelectedLineState(real[0] ?? ALL_LINES);
  }, [baseOrders]);

  const setColumnFilter = useCallback((field: SortField, kind: ColumnFilterKind, value: string) => {
    setColumnFilters(prev => {
      const rest = prev.filter(f => f.field !== field);
      if (!value) return rest;
      return [...rest, { field, kind, value, enabled: true }];
    });
  }, []);
  const clearColumnFilter = useCallback((field: SortField) => {
    setColumnFilters(prev => prev.filter(f => f.field !== field));
  }, []);
  const clearAllFilters = useCallback(() => setColumnFilters([]), []);

  const setColumnSort = useCallback((field: SortField, direction: SortDirection) => {
    setActiveSort({ field, direction });
    setIsCustomOrder(true);
    setOrders(prev => recalcComponentStatus(
      sortByCriterios(prev, [{ field, label: '', direction, active: true, order: 0 }]),
      poolsRef.current,
    ));
  }, []);

  const applyPriorityConfig = useCallback((config: PriorityConfig) => {
    setIsCustomOrder(true);
    setActiveSort(null);
    setPriorityConfig(config);
    setOrders(prev => recalcComponentStatus(sortByCriterios(prev, config.criterios), poolsRef.current));
  }, []);

  const resetToAutoOrder = useCallback(() => {
    setIsCustomOrder(false);
    setActiveSort(null);
    setPriorityConfig(defaultPriorityConfig);
    setOrders(prev => recalcComponentStatus(sortOrders(prev), poolsRef.current));
  }, []);

  const reorderByDragDrop = useCallback((fromId: string, toId: string) => {
    setOrders(prev => {
      const fromIdx = prev.findIndex(o => o.id === fromId);
      const toIdx = prev.findIndex(o => o.id === toId);
      if (fromIdx < 0 || toIdx < 0 || fromIdx === toIdx) return prev;
      // No permitir mover dentro/encima del bloque terminado.
      if (FINISHED_STATUSES.has(prev[fromIdx].estado)) return prev;
      const next = [...prev];
      const [moved] = next.splice(fromIdx, 1);
      next.splice(toIdx, 0, { ...moved, isManuallyReordered: true });
      setIsCustomOrder(true);
      return recalcComponentStatus(next, poolsRef.current);
    });
  }, []);

  const updateEditableField = useCallback(
    (orderId: string, field: 'comentarios' | 'campoCalidad', value: string | boolean | null) => {
      const key = `${orderId}:${field}`;
      let empresa = '';
      let prevValue: string | boolean | null = null;
      setOrders(prev => prev.map(o => {
        if (o.id !== orderId) return o;
        empresa = o.empresa;
        prevValue = o[field] as string | boolean | null;
        return { ...o, [field]: value };
      }));
      setPendingSync(prev => new Set(prev).add(key));

      const body: OverlayBody = { empresa, [field]: value } as OverlayBody;
      secuenciaApi.upsertOverlay(orderId, body)
        .then(() => {
          setPendingSync(prev => { const n = new Set(prev); n.delete(key); return n; });
          // Auditoría: guarda el cambio del overlay (comentario/calidad).
          auditApi.log({ accion: 'comentario', ruta: '/secuencia-produccion', detalle: `${field} · OT ${orderId}`, resultado: 'ok' });
        })
        .catch(() => {
          // Revierte el cambio optimista y limpia el indicador.
          setOrders(prev => prev.map(o => (o.id === orderId ? { ...o, [field]: prevValue } : o)));
          setPendingSync(prev => { const n = new Set(prev); n.delete(key); return n; });
          auditApi.log({ accion: 'comentario', ruta: '/secuencia-produccion', detalle: `${field} · OT ${orderId}`, resultado: 'error' });
        });
    }, []);

  // Actualiza la config de UNA línea concreta (la lista por línea edita cualquiera).
  const updateLineConfigFor = useCallback((lineId: LineId, partial: Partial<LineConfig>) => {
    setLineConfigs(prev => {
      const cur = prev[lineId] ?? { ...FALLBACK_CONFIG, lineId };
      return { ...prev, [lineId]: { ...cur, ...partial } };
    });
  }, []);
  // Compat: edita la línea activa (delegando en updateLineConfigFor).
  const updateLineConfig = useCallback((partial: Partial<LineConfig>) => {
    updateLineConfigFor(selectedLine, partial);
  }, [updateLineConfigFor, selectedLine]);

  const selectOrder = useCallback((id: string | null) => setSelectedOrderId(id), []);

  const triggerRecalc = useCallback((orderId: string) => {
    if (selectedLine === ALL_LINES) return;
    const cfg = lineConfigs[selectedLine];
    if (!cfg || calcularCapacidadDiaria(cfg) <= 0) return;
    // Usa el snapshot actual de `orders` (en deps) para conocer el orden de la
    // secuencia; selectedOrderId se limpia tras recalcular, evitando dobles disparos.
    const lineOrders = orders.filter(o => o.lineId === selectedLine);
    const fromIndex = lineOrders.findIndex(o => o.id === orderId);
    if (fromIndex < 0) return;
    const now = new Date();
    const hoy = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    const { updatedOrders, results } = recalcularSecuencia(lineOrders, fromIndex, cfg, calendar, hoy);
    const patched = new Map(updatedOrders.map(o => [o.id, o]));
    setOrders(prev => prev.map(o => patched.get(o.id) ?? o));
    setLastRecalcResults(results);
    setIsRecalcPending(true);
    setIsCustomOrder(true);
    setSelectedOrderId(null);
  }, [orders, selectedLine, lineConfigs, calendar]);

  const resetRecalc = useCallback(() => {
    const orig = originalDatesRef.current;
    setOrders(prev => prev.map(o => o.isRecalculated
      ? { ...o, fechaPlanificadaFin: orig.get(o.id) ?? o.fechaPlanificadaFin, isRecalculated: false, recalcDelta: undefined }
      : o));
    setLastRecalcResults([]);
    setIsRecalcPending(false);
    setIsCustomOrder(false);
  }, []);

  const setReorderMode = useCallback((on: boolean) => setIsReorderMode(on), []);

  // Calendario laboral: alternar un día (inmutable) y resetear al seed.
  const toggleCalendarDay = useCallback((date: string) => {
    setCalendarDays(prev => toggleDayOverride(prev, date));
  }, []);
  const resetCalendar = useCallback(() => setCalendarDays(seedCalendar2026), []);

  // ── Derivados (sin useEffect para cálculos) ────────────────────────────────
  const lineConfig = useMemo<LineConfig>(
    () => lineConfigs[selectedLine] ?? { ...FALLBACK_CONFIG, lineId: selectedLine },
    [lineConfigs, selectedLine],
  );
  const canRecalc = useMemo(
    () => selectedLine !== ALL_LINES && calcularCapacidadDiaria(lineConfig) > 0,
    [selectedLine, lineConfig],
  );
  const selectedOrder = useMemo(
    () => orders.find(o => o.id === selectedOrderId) ?? null,
    [orders, selectedOrderId],
  );
  const ordersAtRisk = useMemo(() => {
    const s = new Set<string>();
    for (const o of orders) {
      if (o.isRecalculated && o.fechaPedido && o.fechaPlanificadaFin && o.fechaPlanificadaFin > o.fechaPedido) { // Comparación lexicográfica de fechas ISO YYYY-MM-DD (equivale a orden cronológico).
        s.add(o.id);
      }
    }
    return s;
  }, [orders]);

  const filtered = useMemo(
    () => orders.filter(o => columnFilters.every(f => matchesFilter(o, f))),
    [orders, columnFilters],
  );

  const byLine = useMemo(() => {
    // Filtro de modo: Fabricación → solo FABRICACION; Producción → excluye FABRICACION
    // (también en "Todas").
    const base = filtered.filter(o =>
      sequenceMode === 'fabricacion' ? o.lineId === FABRICACION_LINE : o.lineId !== FABRICACION_LINE,
    );
    return selectedLine === ALL_LINES ? base : base.filter(o => o.lineId === selectedLine);
  }, [filtered, selectedLine, sequenceMode]);

  // En modo reordenar: solo las arrastrables (oculta terminadas/histórico).
  const visibleOrders = useMemo(
    () => (isReorderMode ? byLine.filter(o => REORDERABLE_STATUSES.has(o.estado)) : byLine),
    [byLine, isReorderMode],
  );

  const kpis = useMemo(() => computeKpis(byLine), [byLine]);

  return {
    orders, visibleOrders, selectedLine, lineConfig, kpis, isCustomOrder, isReorderMode,
    isLoading: secQ.isLoading || compQ.isLoading || invQ.isLoading,
    isError: secQ.isError || compQ.isError || invQ.isError,
    columnFilters, activeSort, priorityConfig,
    setSelectedLine, setReorderMode,
    setColumnFilter, clearColumnFilter, clearAllFilters, setColumnSort,
    applyPriorityConfig, resetToAutoOrder, reorderByDragDrop,
    updateEditableField, pendingSync, updateLineConfig, lineConfigs, updateLineConfigFor,
    selectedOrderId, selectedOrder, selectOrder, triggerRecalc, resetRecalc,
    isRecalcPending, lastRecalcResults, ordersAtRisk, canRecalc,
    calendarDays, toggleCalendarDay, resetCalendar,
    lines,
    sequenceMode, setSequenceMode,
  };
}
